import { useEffect, useState } from "react";
import { DrawerForm } from "../../../../components/drawers/DrawerForm";
import Icon from "../../../../components/base/Icon";
import Indicator from "../../../../components/base/Indicator";
import { toastError, toastSuccess } from "../../../../components/base/Toast";
import { useHub } from "../../../../core/Global";
import PaymentMethod from "../models/PaymentMethod";
import PaymentCardView from "./PaymentCardView";
import NewCardOption from "./NewCardOption";


const AMOUNTS = [25, 50, 100, 150, 200, 250, 300]
const NEW_CARD = "__new_card__"


interface IProps {
    onClose?: () => void
    onSaved?: () => void
}

export default function WalletChargeDrawer(props: IProps) {

    const hub = useHub()

    const [amount, amountSet] = useState<number>(50)
    const [methods, methodsSet] = useState<PaymentMethod[]>([])
    const [selectedMethodId, selectedMethodIdSet] = useState<string>()
    const [fetching, fetchingSet] = useState<boolean>(true)

    useEffect(() => {
        hub.Get("/accounts/profile/paymentmethods/", res => {
            const data: PaymentMethod[] = res?.data ?? []
            methodsSet(data)
            const primary = data.find(m => m.isPrimary) ?? data[0]
            selectedMethodIdSet(primary?.id ?? NEW_CARD)
            fetchingSet(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <DrawerForm id="drawer-wallet-charge"
            title="Add Balance"
            size="medium"
            buttonText={`Add $${amount} to Wallet`}
            onClose={props.onClose}
            onSubmitted={props.onSaved}
            onSubmit={(body, callback) => {

                if (!selectedMethodId) {
                    toastError("Please choose a payment method.")
                    callback!()
                    return
                }

                // New card: send the user to Stripe hosted Checkout to enter card details and pay.
                if (selectedMethodId === NEW_CARD) {
                    hub.Post("/accounts/profile/wallet/charge/newcard", { amount }, res => {
                        if (res.success && res.data?.url) {
                            document.location.href = res.data.url
                            return
                        }
                        toastError(res.message ?? "Couldn't start the payment.")
                        callback!(res)
                    })
                    return
                }

                hub.Post("/accounts/profile/wallet/charge", {
                    amount,
                    paymentMethodId: selectedMethodId
                }, res => {
                    // Card needs 3-D Secure: send the user to Stripe to enter the code. They are
                    // redirected back to the wallet page once the charge is finalized.
                    if (res.success && res.data?.requiresAction && res.data?.redirectUrl) {
                        document.location.href = res.data.redirectUrl
                        return
                    }

                    if (res.success) toastSuccess(`$${amount} has been added to your wallet successfully!`)
                    else toastError(res.message ?? "Charge failed!")
                    callback!(res)
                })
            }}>

            <div className="mb-8">
                <label className="fs-6 fw-bold text-gray-800 mb-3 d-block">Select Amount</label>
                <div className="d-flex flex-wrap gap-2">
                    {
                        AMOUNTS.map(a =>
                            <button key={a} type="button"
                                className={`btn btn-sm ${amount === a ? "btn-primary" : "btn-light"}`}
                                style={{ minWidth: 70 }}
                                onClick={() => amountSet(a)}>
                                ${a}
                            </button>
                        )
                    }
                </div>
                <div className="text-gray-500 fs-8 mt-2">Choose an amount between $25 and $300.</div>
            </div>

            <div className="mb-4">
                <label className="fs-6 fw-bold text-gray-800 mb-3 d-block">Payment Method</label>

                {
                    fetching ?
                        <div className="text-center py-8">
                            <Indicator />
                        </div>
                        :
                        <div className="d-flex flex-column gap-3">

                            {
                                methods.map(m => {
                                    const selected = selectedMethodId === m.id
                                    return (
                                        <div key={m.id}
                                            className={`d-flex align-items-center p-4 rounded border cursor-pointer ${selected ? "border-primary bg-light-primary" : "border-gray-300"}`}
                                            onClick={() => selectedMethodIdSet(m.id)}>
                                            <PaymentCardView item={m} />
                                            <div className="ms-auto">
                                                <Icon
                                                    icon={selected ? "circle-check" : "circle"}
                                                    type="solid"
                                                    size={20}
                                                    className={selected ? "text-primary" : "text-gray-400"} />
                                            </div>
                                        </div>
                                    )
                                })
                            }

                            <NewCardOption
                                selected={selectedMethodId === NEW_CARD}
                                onClick={() => selectedMethodIdSet(NEW_CARD)} />
                        </div>
                }
            </div>

        </DrawerForm>
    )
}
